import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";

import { Phone, CheckCircle, XCircle, Plus, RefreshCw } from "lucide-react";

import { StatsCard } from "./StatsCard";
import { FilterBar } from "./FilterBar";
import { DIDTable } from "./DIDTable";
import { DIDFormDialog } from "./DIDFormDialog";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axios from "axios";
import axiosInstance from "../../../services/axiosInstance";

const TableComponent = () => {
  const [dids, setDids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [formOpen, setFormOpen] = useState(false);
  const [editingDid, setEditingDid] = useState(null);

  const [formValues, setFormValues] = useState({
    did_alias: "",
    country: "",
    did_isAvailable: "yes",
    userId: "",
    did_id: "",
  });
  const fetchDids = async (controller) => {
    try {
      setLoading(true);
      setError(null);

      const res = await axiosInstance.get(apiEndPointsConfig.getAllDids, {
        signal: controller.signal,
      });

      if (!res.data.success) {
        throw new Error(res.data.message);
      }

      setDids(res.data.data || []);
    } catch (err) {
      if (err.name !== "CanceledError" && err.name !== "AbortError") {
        setError(err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchDids(controller);
    return () => controller.abort();
  }, []);

  // Refresh
  const handleRefresh = () => {
    const controller = new AbortController();
    fetchDids(controller);
  };

  // ----- FORM -----
  const openAddForm = () => {
    setEditingDid(null);
    setFormValues({
      did_alias: "",
      country: "",
      did_isAvailable: "yes",
      userId: "",
      did_id: "",
    });
    setFormError("");
    setFormOpen(true);
  };

  const openEditForm = (did) => {
    setEditingDid(did);
    setFormValues({
      did_alias: did.did_alias,
      country: did.country,
      did_isAvailable: did.did_isAvailable,
      userId: did.userId,
      did_id: did.did_id,
    });
    setFormError("");
    setFormOpen(true);
  };

  const handleFormChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFormClose = () => {
    if (!saving) {
      setFormOpen(false);
      setEditingDid(null);
    }
  };

  const handleFormSubmit = async () => {
    setFormError("");

    // Basic validation
    if (!formValues.did_alias || !formValues.country) {
      setFormError("DID Number & Country are required.");
      return;
    }

    try {
      setSaving(true);

      const url = editingDid
        ? `${apiEndPointsConfig.adminDid}/${editingDid._id}`
        : apiEndPointsConfig.adminDid;
      const method = editingDid ? "PUT" : "POST";

      const res = await axiosInstance({
        url,
        method,
        data: formValues
      });

      if (!res.data.success) throw new Error(res.data.message);

      await fetchDids(new AbortController());
      setFormOpen(false);
      setEditingDid(null);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ----- FILTER -----
  const countries = useMemo(
    () => [...new Set(dids.map((d) => d.country).filter(Boolean))].sort(),
    [dids]
  );

  const filteredData = useMemo(() => {
    let data = dids;

    if (searchTerm) {
      data = data.filter(
        (d) =>
          d.did_alias.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.country.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (countryFilter !== "all") {
      data = data.filter((d) => d.country === countryFilter);
    }

    if (availabilityFilter !== "all") {
      const wantAvailable = availabilityFilter === "available";
      data = data.filter((d) =>
        wantAvailable
          ? d.did_isAvailable.toLowerCase() === "yes"
          : d.did_isAvailable.toLowerCase() !== "yes"
      );
    }

    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        let aVal = a[sortConfig.key]?.toString().toLowerCase();
        let bVal = b[sortConfig.key]?.toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [dids, searchTerm, countryFilter, availabilityFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Stats summary
  const stats = {
    total: dids.length,
    available: dids.filter((d) => d.did_isAvailable.toLowerCase() === "yes")
      .length,
    unavailable: dids.filter((d) => d.did_isAvailable.toLowerCase() !== "yes")
      .length,
  };

  // Reset filters
  const clearFilters = () => {
    setSearchTerm("");
    setCountryFilter("all");
    setAvailabilityFilter("all");
  };

  // ----- UI -----
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ minHeight: "100vh", py: 4 }}>
        <Container maxWidth="xl">
          {/* Stats */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={4}>
              <StatsCard
                icon={Phone}
                label="Total DIDs"
                value={stats.total}
                color="#3b82f6"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard
                icon={CheckCircle}
                label="Available"
                value={stats.available}
                color="#22c55e"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard
                icon={XCircle}
                label="Unavailable"
                value={stats.unavailable}
                color="#ef4444"
              />
            </Grid>
          </Grid>

          {/* Filters */}
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            countryFilter={countryFilter}
            openAddForm={openAddForm}
            handleRefresh={handleRefresh}
            onCountryChange={setCountryFilter}
            availabilityFilter={availabilityFilter}
            onAvailabilityChange={setAvailabilityFilter}
            countries={countries}
            filteredCount={filteredData.length}
            totalCount={dids.length}
            onClearFilters={clearFilters}
          />

          {/* Table */}
          <DIDTable
            data={filteredData}
            sortConfig={sortConfig}
            onSort={handleSort}
            onEdit={openEditForm}
          />

          {/* Form */}
          <DIDFormDialog
            open={formOpen}
            onClose={handleFormClose}
            onSubmit={handleFormSubmit}
            formValues={formValues}
            onFormChange={handleFormChange}
            editingDid={editingDid}
            saving={saving}
            error={formError}
          />
        </Container>
      </Box>
    </Fade>
  );
};

export default TableComponent;
